import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, signInWithCustomToken, signOut, onIdTokenChanged, GoogleAuthProvider, signInWithPopup, User as FirebaseUser } from 'firebase/auth';
import { auth } from '@clareefai/api/firebase';
import {
  clearImpersonationSessionId,
  getImpersonationSessionId,
  isImpersonationSessionExpired,
} from '@clareefai/api/impersonation';
import { endImpersonationSession } from '@clareefai/api/impersonation-hooks';
import { User } from '@clareefai/types';
import { getFirebaseAuthErrorMessage } from '@clareefai/utils';
import { store } from '../store';
import { vendorApi } from '../store/vendorApi';
import { promoterApi } from '../store/promoterApi';
import { prospectApi } from '../store/prospectApi';
import { meetingApi } from '../store/meetingApi';
import { rewardApi } from '../store/rewardApi';
import { teamMemberApi } from '../store/teamMemberApi';
import { emailTemplateApi } from '../store/emailTemplateApi';

const TEAM_MEMBER_ROLES = ['team_sales', 'team_customer_success', 'team_account_manager', 'team_manager', 'team_marketing_manager'];

export const isTeamMemberRole = (role: string | undefined): boolean =>
  !!role && TEAM_MEMBER_ROLES.includes(role);

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; role?: string }>;
  loginWithCustomToken: (customToken: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  // Exchanges a Google ID token for a Clareefai custom token via /auth/google-exchange.
  // ok=false means no linked vendor account exists (or the exchange failed).
  exchangeGoogleIdToken: (idToken: string) => Promise<{ ok: boolean; status: number; customToken?: string }>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  firebaseUser: FirebaseUser | null;
  role: string | null;
  isLoading: boolean;
  isTeamMember: boolean;
  isImpersonating: boolean;
  impersonationSessionId: string | null;
  exitImpersonation: () => Promise<void>;
  // Set by AuthDataSync when the post-sign-in profile fetch (/vendors/me or
  // /team-members/me) fails outright — lets screens that wait on isAuthenticated
  // (e.g. the trial signup spinner) bail out with a message instead of hanging
  // forever waiting for a session that will never resolve.
  authError: string | null;
  setAuthError: (error: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const resetAllApiState = () => {
  store.dispatch(vendorApi.util.resetApiState());
  store.dispatch(promoterApi.util.resetApiState());
  store.dispatch(prospectApi.util.resetApiState());
  store.dispatch(meetingApi.util.resetApiState());
  store.dispatch(rewardApi.util.resetApiState());
  store.dispatch(teamMemberApi.util.resetApiState());
  store.dispatch(emailTemplateApi.util.resetApiState());
};

const STORED_UID_KEY = 'firebase_auth_uid';

/** All localStorage keys owned by the vendor app. Cleared on logout and on user switch. */
const USER_SESSION_KEYS = [
  'clareef_user',
  'onboardingData',
  'onboarding_draft',
  'clareefai_page_data',
  STORED_UID_KEY,
];

const loadStoredUser = (): User | null => {
  try {
    const stored = localStorage.getItem('clareef_user');
    if (!stored) return null;
    return JSON.parse(stored) as User;
  } catch {
    return null;
  }
};

/** Clears all user-specific localStorage/sessionStorage data and RTK Query cache. */
const clearUserSession = () => {
  USER_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem('onboardingDeferred');
  resetAllApiState();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(loadStoredUser);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [impersonationSessionId, setImpersonationSessionIdState] = useState<string | null>(
    () => getImpersonationSessionId()
  );

  useEffect(() => {
    // onIdTokenChanged (not onAuthStateChanged) — the trial signup flow re-authenticates
    // the *same* Firebase UID via signInWithCustomToken right after a Google popup
    // sign-in (to attach the freshly-minted role claim). Firebase treats that as a
    // token refresh, not a sign-in state transition, so onAuthStateChanged never
    // fires again and the role claim is never picked up. onIdTokenChanged is a
    // superset that also fires for same-user claim/token refreshes.
    const unsubscribe = onIdTokenChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      setAuthError(null);

      if (fbUser) {
        try {
          // The Firebase ID token is the authoritative signal for whether the
          // current session is an impersonation. If localStorage is out of
          // sync with the token (cleared, mismatched session id, or past
          // expiry), force-exit — the target's data is no longer the admin's
          // to look at.
          const idTokenResult = await fbUser.getIdTokenResult();
          const tokenIsImpersonation = idTokenResult.claims.impersonation === true;
          const tokenSessionId = idTokenResult.claims.sessionId as string | undefined;
          const localSessionId = getImpersonationSessionId();
          const desynced = tokenIsImpersonation && (!localSessionId || localSessionId !== tokenSessionId);
          if (desynced || isImpersonationSessionExpired()) {
            console.warn(
              '[Auth] Impersonation session is stale or expired; signing out.',
              { tokenIsImpersonation, localSessionId, tokenSessionId }
            );
            clearImpersonationSessionId();
            await signOut(auth);
            setImpersonationSessionIdState(null);
            setUser(null);
            setRole(null);
            localStorage.removeItem('firebase_id_token');
            clearUserSession();
            setAuthChecked(true);
            return;
          }
          const token = await fbUser.getIdToken();
          localStorage.setItem('firebase_id_token', token);
          const r = idTokenResult.claims.role as string;
          // onIdTokenChanged also fires on every ~1h token refresh, so don't log full
          // claims to the console in production. Dev-only.
          if (import.meta.env.DEV) {
            console.log('[Auth] Firebase token claims (on login / auth state):', {
              uid: fbUser.uid,
              email: fbUser.email,
              role: r,
              allClaims: idTokenResult.claims,
            });
          }

          // If a different user is logging in, clear the previous user's data and cache
          // to prevent stale onboardingCompleted / vendor data from leaking across sessions.
          const storedUid = localStorage.getItem(STORED_UID_KEY);
          if (storedUid && storedUid !== fbUser.uid) {
            clearUserSession();
            setUser(null);
          }
          localStorage.setItem(STORED_UID_KEY, fbUser.uid);

          if (r === 'vendor' || TEAM_MEMBER_ROLES.includes(r)) {
            setRole(r);
            setUser(loadStoredUser());
          } else {
            console.error('Unknown role:', r);
            setRole(null);
            setUser(null);
          }

          setImpersonationSessionIdState(getImpersonationSessionId());
        } catch (error) {
          console.error('Error getting token:', error);
          setRole(null);
          setUser(null);
          setAuthError('We could not verify your session. Please try again.');
        }
      } else {
        setUser(null);
        setRole(null);
        localStorage.removeItem('firebase_id_token');
        clearUserSession();
        // Do NOT clear the impersonation localStorage here. This branch fires
        // transiently between sign-ins (e.g. while an exchange is mid-flight),
        // and wiping the freshly-set session id would break the new sign-in.
        // Explicit logout / exitImpersonation already clear it.
        setImpersonationSessionIdState(null);
      }
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; role?: string }> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const r = idTokenResult.claims.role as string;
      if (import.meta.env.DEV) {
        console.log('[Auth] Firebase claims (on login):', {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          role: r,
          allClaims: idTokenResult.claims,
        });
      }
      const allowedRoles = ['vendor', ...TEAM_MEMBER_ROLES];
      if (!allowedRoles.includes(r)) {
        await signOut(auth);
        return { success: false, error: 'Access denied. Vendor or team member role required.' };
      }
      localStorage.setItem('firebase_id_token', await userCredential.user.getIdToken());
      return { success: true, role: r };
    } catch (error: unknown) {
      return { success: false, error: getFirebaseAuthErrorMessage(error) };
    }
  };

  const loginWithCustomToken = async (customToken: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const userCredential = await signInWithCustomToken(auth, customToken);
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const r = idTokenResult.claims.role as string;
      const allowedRoles = ['vendor', ...TEAM_MEMBER_ROLES];
      if (!allowedRoles.includes(r)) {
        await signOut(auth);
        return { success: false, error: 'Access denied. Vendor or team member role required.' };
      }
      localStorage.setItem('firebase_id_token', await userCredential.user.getIdToken());
      return { success: true };
    } catch (error: unknown) {
      return { success: false, error: getFirebaseAuthErrorMessage(error) };
    }
  };

  const exchangeGoogleIdToken = async (
    idToken: string,
  ): Promise<{ ok: boolean; status: number; customToken?: string }> => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
    const res = await fetch(`${baseUrl}/auth/google-exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken, app: 'vendor' }),
    });
    if (!res.ok) return { ok: false, status: res.status };
    const data = await res.json();
    return { ok: true, status: res.status, customToken: data.custom_token };
  };

  const loginWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const googleIdToken = await result.user.getIdToken();

      const exchange = await exchangeGoogleIdToken(googleIdToken);
      if (!exchange.ok || !exchange.customToken) {
        await signOut(auth);
        const error =
          exchange.status === 403
            ? 'No vendor account is linked to this Google email.'
            : 'Google sign-in failed. Please try again.';
        return { success: false, error };
      }

      return await loginWithCustomToken(exchange.customToken);
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return { success: false };
      }
      return { success: false, error: getFirebaseAuthErrorMessage(error) };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole(null);
      localStorage.removeItem('firebase_id_token');
      clearUserSession();
      clearImpersonationSessionId();
      setImpersonationSessionIdState(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const exitImpersonation = async () => {
    try {
      const token = (await auth.currentUser?.getIdToken()) ?? null;
      await endImpersonationSession(
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api',
        token
      );
    } finally {
      setImpersonationSessionIdState(null);
      await logout();
    }
  };

  const setUserData = useCallback((userData: User | null) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('clareef_user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('clareef_user');
    }
  }, []);

  const isTeamMember = isTeamMemberRole(role ?? undefined);
  const isLoading =
    !authChecked ||
    (!!firebaseUser && !user && (role === 'vendor' || isTeamMember));

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loginWithCustomToken,
        loginWithGoogle,
        exchangeGoogleIdToken,
        logout,
        setUser: setUserData,
        isAuthenticated: !!user && !!firebaseUser,
        firebaseUser,
        role,
        isLoading,
        isTeamMember,
        isImpersonating: !!impersonationSessionId,
        impersonationSessionId,
        exitImpersonation,
        authError,
        setAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
